/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
  DatePicker,
} from 'antd';
import {
  DeleteOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  deleteExpedienteComercioInformal,
  getExpedienteComercioInformal,
  getExpedientesInformales,
  patchExpedienteComercioInformal,
  postExpedienteComercioInformal,
} from 'api/empresas/expedienteDeComercioInformal';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { getGirosComercioInformales } from 'api/empresas/giroDeComercioInformal';
import { getDias } from 'api/catalogos';
import { getContribuyente, getContribuyenteCaja } from 'api/empresas/contribuyentes';
import NumericInput from 'components/NumericInput';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { getPerimetros } from 'api/catalogos/perimetros';
import Direccion from 'components/Direccion';
import { getPuestosDeComercios } from 'api/catalogos/puestosDeComercioInformal';
import { capitalize } from 'utils/normalizers';

const { TabPane } = Tabs;
const baseURI = 'empresas/expedientes-de-comercio-informal/';

const models = ['expedientedecomercioinformal'];

const ExpedienteDeComercioInformal = ({ permission }) => {
  const { estadosGlobales, dias } = useSelector(({ catalogs }) => catalogs);
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [formAddress, setFormAddress] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [giros, setGiros] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [contribuyenteFound, setContribuyenteFound] = useState([]);
  const [perimetros, setPerimetros] = useState([]);
  const [tipoDePuesto, setTipoDePuesto] = useState([]);
  const [visibleSearchOnFilter, setVisibleSearchOnFilter] = useState(false);
  const searchRef = useRef();

  const basicKeys = ['general'];
  const complexForms = [
    {
      key: 'direccion',
      formInstance: formAddress,
      permission: addressPermission,
    }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getExpedientesInformales({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_giros, _perimetros, _puestos] = await Promise.all([
        getGirosComercioInformales(),
        getPerimetros(),
        getPuestosDeComercios(),
        getDias(),
      ]);
      if (mounted) {
        setGiros(_giros);
        setPerimetros(_perimetros);
        setTipoDePuesto(_puestos);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const searchContribuyente = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q_contribuyente']);
      const { q_contribuyente: q } = form.getFieldsValue();
      if (!q) {
        message.info('Ingrese algún dato para buscar al Contribuyente.');
        return;
      }
      const contribuyentes = await getContribuyenteCaja({ rfc: q });

      if (!contribuyentes?.results?.length) {
        message.info('No se encontró al Contribuyente.');
        return;
      }

      const [selected] = contribuyentes.results;
      form.setFieldsValue({
        contribuyente: selected.id,
        clave_contribuyente: selected.clave,
        nombre_completo: selected.nombre_completo,
      });
      setContribuyenteFound([selected]);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const fetchContribuyente = async (id, altern = false) => {
    if (!id) return;
    setLoading(true);
    const _contribuyente = await getContribuyente(id?.id || id);
    if (!_contribuyente) {
      message.info('No se encontró al contribuyente');
      setContribuyenteFound([]);
      setLoading(false);
      return;
    }
    setContribuyenteFound([_contribuyente]);
    setTimeout(() => {
      if (altern) {
        formSearch.setFieldsValue({
          contribuyente: _contribuyente?.id,
          clave_contribuyente: _contribuyente?.clave,
          nombre_completo: _contribuyente?.nombre_completo,
        }, 100);
      } else {
        form.setFieldsValue({
          contribuyente: _contribuyente?.id,
          clave_contribuyente: _contribuyente?.clave,
          nombre_completo: _contribuyente?.nombre_completo,
        }, 100);
      }
    });
    setLoading(false);
  };

  const resetAndValidate = () => {
    setContribuyenteFound([]);
    form.resetFields(['q', 'nombre_completo', 'clave_contribuyente']);
    form.validateFields(['q']);
    formSearch.resetFields(['q']);
    formSearch.validateFields(['q']);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setContribuyenteFound([]);
    setCurrentTabKey('general');
  };

  const onClickEdit = async (ExpedienteDeComercioInformalId) => {
    const key = selectedRowKeys[0] || ExpedienteDeComercioInformalId;
    const match = await getExpedienteComercioInformal(key);
    if (match) {
      await fetchContribuyente(match?.contribuyente?.id ?? match?.contribuyente);
    }
    form.setFieldsValue(match);
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const _data = await (
        key ? patchExpedienteComercioInformal(key, values) : postExpedienteComercioInformal(values)
      );
      if (_data) {
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteExpedienteComercioInformal(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Propietario',
      dataIndex: 'contribuyente',
      key: 'contribuyente',
      render: (val) => (val?.nombre_completo),
      width: 200,
    },
    {
      titleText: 'Giros',
      dataIndex: 'giros',
      key: 'giros',
      render: (val) => {
        const _giros = val.map((e) => giros?.find((i) => i.id === e)?.descripcion);
        return _giros?.map((e) => ` ${e}`).toString();
      },
      width: 200,
    },
    {
      titleText: 'Tipo de puesto',
      key: 'tipo_puesto',
      dataIndex: 'tipo_puesto',
      width: 200,
      catalog: {
        cat: tipoDePuesto,
        use: true,
      },
    },
    {
      titleText: 'Perimetro',
      dataIndex: 'perimetro',
      key: 'perimetro',
      width: 200,
      catalog: {
        cat: perimetros,
        use: true,
      },
    },
    {
      titleText: 'Estatus de Expediente',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const wipe = () => {
    formSearch.resetFields();
    setData({ results: [] });
  };

  const handleSearchData = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields();
      const searchParams = formSearch.getFieldsValue();
      const keys = Object.keys(searchParams);
      const validProps = keys.filter((prop) => searchParams[prop]);

      if (!validProps?.length) {
        message.warn('Debe ingresar al menos un campo.');
        return;
      }

      const _data = await getExpedientesInformales(searchParams);
      setData(_data);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = () => {
    if (searchRef.current) {
      clearTimeout(searchRef.current);
    }
    searchRef.current = setTimeout(handleSearchData, 500);
  };

  const alternSearchContribuyente = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields(['q_contribuyente']);
      const { q_contribuyente: q } = formSearch.getFieldsValue();
      if (!q) {
        message.info('Ingrese algún dato para buscar al Contribuyente.');
        return;
      }
      const contribuyentes = await getContribuyenteCaja({ rfc: q });

      if (!contribuyentes?.results?.length) {
        message.info('No se encontró al Contribuyente.');
        return;
      }

      const [selected] = contribuyentes.results;
      formSearch.setFieldsValue({
        contribuyente: selected.id,
      });
      setContribuyenteFound([selected]);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <>
              <Form
                layout="vertical"
                form={formSearch}
                scrollToFirstError
                onFinish={() => debouncedSearch()}
              >
                <Row gutter={10}>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Clave"
                      name="clave"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Denominación"
                      name="denominacion"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Giro"
                      name="giros"
                      normalize={capitalize}
                    >
                      <Select
                        dataSource={giros}
                      />
                    </Form.Item>
                  </Col>
                  <InputSearchPro
                    label="RFC de contribuyente"
                    name="q_contribuyente"
                    onClickAdvanced={() => setVisibleSearchOnFilter(true)}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 8,
                    }}
                    inputProps={{
                      onPressEnter: () => alternSearchContribuyente(),
                      onChange: ({ target: { value } }) => {
                        if (!value) {
                          resetAndValidate();
                        }
                      },
                    }}
                    resultProps={{
                      label: 'RFC',
                      name: 'contribuyente',
                      dataSource: contribuyenteFound,
                      labelProp: 'rfc',
                      onClear: resetAndValidate,
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 8,
                      },
                    }}
                  />
                  <Col xs={24} sm={24} md={8}>
                    <Button
                      htmlType="submit"
                      block
                      style={{ marginTop: 21 }}
                      icon={<SearchOutlined />}
                    >
                      Buscar
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Button
                      onClick={wipe}
                      block
                      style={{ marginTop: 21 }}
                      icon={<DeleteOutlined />}
                    >
                      Limpiar Busqueda
                    </Button>
                  </Col>
                </Row>
              </Form>

              <Table
                cols={columns}
                mobileColumns={[columns[0], columns[1]]}
                data={data.results || []}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit,
                  onClickDelete,
                }}
                rowKey="id"
                baseURI={baseURI}
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data.count}
                currentPage={currentPage}
                hasAllViewPermission={hasAllViewPermission}
                deleteText="Cancelar"
                allowSearch={false}
              />
            </>

          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Comercio en Vía Pública`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                      initialValues={{ estados_globales: 1 }}
                    >
                      <Row gutter={10}>
                        <InputSearchPro
                          label="RFC de Contribuyente"
                          name="q_contribuyente"
                          onClickAdvanced={() => setVisibleBusquedaPadrones(true)}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 8,
                          }}
                          inputProps={{
                            onPressEnter: () => searchContribuyente(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            label: 'RFC',
                            name: 'contribuyente',
                            dataSource: contribuyenteFound,
                            labelProp: 'rfc',
                            onClear: resetAndValidate,
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 8,
                            },
                          }}
                        />
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave_contribuyente"
                            label="Clave de contribuyente"
                            hasFeedback
                          >
                            <Input allowClear disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="nombre_completo"
                            label="Nombre de contribuyente"
                            hasFeedback
                          >
                            <Input allowClear disabled />
                          </Form.Item>
                        </Col>
                        {!!selectedRowKeys?.length && (
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave"
                            label="clave"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <Input allowClear disabled />
                          </Form.Item>
                        </Col>
                        )}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="denominacion"
                            label="Denominación"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            name="tipo_puesto"
                            label="Tipo de Puesto"
                            hasFeedback
                          >
                            <Select dataSource={tipoDePuesto} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            name="giros"
                            label="Giro"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Select dataSource={giros} mode="multiple" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="perimetro"
                            label="Perimetro"
                            hasFeedback
                          >
                            <Select dataSource={perimetros} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="num_dias"
                            label="Número de días"
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="mts_largo"
                            label="Largo m2"
                            decimal
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="mts_ancho"
                            label="Ancho m2"
                            decimal
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="area_m2"
                            label="Area m2"
                            decimal
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_alta"
                            label="Fecha de alta"
                            hasFeedback
                          >
                            <DatePicker
                              format={DATE_FRONTEND_FORMAT}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="horario"
                            label="Horario"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="dias_laborales"
                            label="Dias laborales"
                          >
                            <Select dataSource={dias} mode="multiple" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="organizacion"
                            label="Organización"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_adeudo"
                            label="Fecha de último pago"
                            hasFeedback
                          >
                            <DatePicker
                              format={DATE_FRONTEND_FORMAT}
                              // disabled
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estado"
                            hasFeedback
                            rules={rules.required}
                          >
                            <Select
                              disabled
                              dataSource={estadosGlobales}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={24}>
                          <Form.Item
                            name="observaciones"
                            label="Observaciones"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                          </Form.Item>
                        </Col>

                      </Row>
                    </Form>
                  </TabPane>
                  <TabPane
                    tab="Domicilio"
                    key="direccion"
                    disabled={!selectedRowKeys.length}
                    forceRender={selectedRowKeys.length}
                  >
                    <Direccion
                      currentURI={baseURI}
                      parentID={selectedRowKeys[0]}
                      setLoading={setLoading}
                      onError={onError}
                      setFormAddress={setFormAddress}
                      setCurrentTabKey={setCurrentTabKey}
                      addressPropName="direccion"
                      callback={(values, _continue) => {
                        if (!_continue) {
                          onCancel();
                        } else {
                          const [key] = selectedRowKeys;
                          const clone = [...data.results];
                          const mappedData = clone.map((e) => (e.id === key
                            ? ({ ...e, direccion: values?.id }) : e));
                          setData(mappedData);
                        }
                      }}
                      permission={addressPermission}
                    />
                  </TabPane>
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Expediente de comercio informar seleccionado"
          loading={loading}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={fetchContribuyente}
          tipoDePadron={15}
          allowAdd={false}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleSearchOnFilter}
          setVisible={setVisibleSearchOnFilter}
          tipoDePadron={15}
          allowAdd={false}
          // eslint-disable-next-line max-len
          callback={(_contribuyente) => fetchContribuyente(_contribuyente?.id || _contribuyente, true)}
        />
      </Spin>
    </Row>
  );
};

export default ExpedienteDeComercioInformal;
